// TODO: THIS IS BETTER IMPLEMENTED AS JUST AN EXPOSED UTIL FUNCTION 
// THAT CAN BE CONSUMED BY USERS IN A FUNCTION NODE



/**
 * ArrayGatherNode - Gathers elements from `data` at the given `indices`
 *
 * Outputs data[indices[i]] for each i. Out-of-bounds indices are clamped.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const arrayGatherEndpoints = internals({
  data:    { dataType: 'array', isInput: true, label: 'Data' },
  indices: { dataType: 'array', isInput: true, label: 'Indices' },
  output:  { dataType: 'array', isOutput: true, label: 'Output' },
});

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class ArrayGatherNode extends BaseNode<typeof arrayGatherEndpoints> {
  private cachedData: unknown[] | null = null;
  private cachedIndices: number[] | null = null;

  onInternalChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'data') this.cachedData = _payload.value as unknown[];
    else if (endpointId === 'indices') this.cachedIndices = _payload.value as number[];
    this.compute();
  }

  private compute(): void {
    if (!this.cachedData || !this.cachedIndices) return;

    const data = this.cachedData;
    const len = data.length;
    if (len === 0) {
      this.internals.output.set([]);
      return;
    }

    const result: unknown[] = [];
    for (const idx of this.cachedIndices) {
      const clamped = Math.max(0, Math.min(len - 1, Math.floor(idx)));
      result.push(data[clamped]);
    }

    this.internals.output.set(result);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const arrayGatherNodeDef: NodeDef<ArrayGatherNode> = {
  type: 'ArrayGather',
  shortName: 'gather',
  info: 'Picks elements from data at the given indices. Out-of-bounds indices are clamped.',
  domain: Domain.Data,
  category: 'Array',
  endpoints: arrayGatherEndpoints,
  ctor: ArrayGatherNode,
};
